import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectPhotoToEdit } from '@redux/selectors/PhotoSelector';
import { addEditPhotoResult, removeEditPhoto, softDeletePhoto } from '@redux/modules/photo';

import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

import Modal from '@components/smartAdminUI/Modal';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import CropperController from './cropperController/CropperController';

const ImageCropperModal = () => {
  const dispatch = useDispatch();
  const cropperRef = useRef(null);
  const [cropping, setCropping] = useState(false); // whether cropper is in the middle of cropping
  const [cropData, setCropData] = useState(null); // cropped image object
  const [cropper, setCropper] = useState(null); // cropper js object
  const photoToEdit = useSelector(selectPhotoToEdit); // gets photo key for editing out of redux

  const [descriptionFieldValue, setDescriptionFieldValue] = useState(''); // value for description text box
  const [displayImageSrc, setDisplayImageSrc] = useState(''); // Full size image for cropper to work with
  const [imageFileName, setImageFileName] = useState('');

  // set description from redux
  useEffect(() => {
    if (photoToEdit) {
      setDescriptionFieldValue(photoToEdit.Description);
      if (photoToEdit.cropperPhoto && typeof (photoToEdit.cropperPhoto) !== 'string') {
        const displaySrc = URL.createObjectURL(photoToEdit.cropperPhoto);
        setDisplayImageSrc(displaySrc);
        setImageFileName(`${photoToEdit.key}.jpeg`);
      }
    }
  }, [photoToEdit]);

  // keep modal state in unison with cropper state
  useEffect(() => {
    if (cropper && cropper.options.dragMode === 'crop') {
      setCropping(true);
    }
  }, [cropper]);

  // writes orientation and scale changes to canvas
  const setTransformData = () => {
    const canvasData = cropper.getCanvasData();
    if (canvasData) {
      cropper.setCropBoxData(canvasData);
      setCropData(cropper.getCroppedCanvas().toDataURL());
    }
  };

  // description modification UI -> formik
  const handleBlur = e => {
    if (!photoToEdit) return;
    if (photoToEdit.Description === e.target.value) return;
    photoToEdit.Description = e.target.value;
    setTransformData();
  };

  // description modification formik -> UI
  const handleChange = e => {
    setDescriptionFieldValue(e.target.value);
    setTransformData();
  };

  const onRotateLeft = () => {
    if (!cropper) return;
    cropper.rotate(-90);
    setTransformData();
  };

  const onRotateRight = () => {
    if (!cropper) return;
    cropper.rotate(90);
    setTransformData();
  };

  const onZoomIn = () => {
    if (!cropper) return;
    cropper.crop();
    cropper.zoom(0.1);
    setTransformData();
  };

  const onZoomOut = () => {
    if (!cropper) return;
    cropper.crop();
    cropper.zoom(-0.1);
    setTransformData();
  };

  const onFlipH = () => {
    if (!cropper) return;
    cropper.scaleX(-cropper.getData().scaleX || 1);
    setTransformData();
  };

  const onFlipV = () => {
    if (!cropper) return;
    cropper.scaleY(-cropper.getData().scaleY || 1);
    setTransformData();
  };

  const onCropImage = action => {
    if (!cropper) return;
    if (cropper !== undefined && action === 'move') {
      cropper.setDragMode('move');
      setCropping(false);
    } else {
      cropper.setDragMode('crop');
      setCropping(true);
    }
  };

  const onSave = async () => {
    if (cropData) {
      dispatch(addEditPhotoResult(photoToEdit, cropData));
      setCropData(URL.revokeObjectURL(cropData));
      setCropping(false);
      setCropData(null);
      dispatch(removeEditPhoto());
    }
  };

  const onReset = () => {
    cropper.reset();
    cropper.clear();
    if (cropData) setCropData(URL.revokeObjectURL(cropData));
    setCropData(null);
    setCropping(false);
  };

  // load crop result
  const onCrop = () => {
    if (!cropper) return;
    setCropData(cropper.getCroppedCanvas().toDataURL());
    setCropping(false);
    cropper.setDragMode('move');
  };

  const closeClicked = () => {
    if (!cropper) return;
    if (cropData) setCropData(URL.revokeObjectURL(cropData));
    setCropData(null);
    setCropping(false);
    dispatch(removeEditPhoto());
  };

  const onRemove = () => {
    if (!cropper) return;
    if (cropData) setCropData(URL.revokeObjectURL(cropData));
    dispatch(softDeletePhoto(photoToEdit));
    dispatch(removeEditPhoto());
  };

  return (
    <Modal
      modalHeader={`Image - ${photoToEdit?.id ?? ''}`}
      size="lg"
      position="center"
      id="ImageCropperModal"
      type="backdropTransparent"
      closeModal={closeClicked}
      onClickDismiss={closeClicked}
    >
      <div className="container">
        {photoToEdit && (
          <div className="imageCropperWrapper">
            <div className="imageCropperNavbar">
              <CropperController
                cropping={cropping}
                onCrop={onCrop}
                photoToEdit={photoToEdit}
                onRemove={onRemove}
                onReset={onReset}
                onSave={onSave}
                imageURL={displayImageSrc}
                imageFileName={imageFileName}
              />
            </div>
            <div className="form-group mb-0 mt-2 imageCropperNavbar">
              <label
                className="form-label"
                htmlFor={`photoId-${photoToEdit.key}-dascription`}
              >
                Description
              </label>
              <input
                className="form-control"
                id={`photoId-${photoToEdit.key}-dascription`}
                name={`photoId-${photoToEdit.key}-dascription`}
                type="text"
                value={descriptionFieldValue ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </div>
            <Cropper
              style={{ maxHeight: '500px', height: 'auto', width: '100%' }}
              src={displayImageSrc}
              viewMode={2}
              responsive
              center
              background={false}
              autoCrop={false}
              checkOrientation={false}
              ref={cropperRef}
              onInitialized={instance => setCropper(instance)}
              dragMode="none"
              guides
            />
            <div>
              <div className="btn-group imageCropperToolbar">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-crop-button" style={{ margin: 0 }}>Crop</Tooltip>}
                >
                  <Button
                    name="btnCropImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={() => onCropImage('drag')}
                  >
                    <i className="far fa-crop-alt" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-zoom-in-button" style={{ margin: 0 }}>Zoom In</Tooltip>}
                >
                  <Button
                    name="btnZoomInImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={onZoomIn}
                  >
                    <i className="far fa-search-plus" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-zoom-out-button" style={{ margin: 0 }}>Zoom Out</Tooltip>}
                >
                  <Button
                    name="btnZoomOutImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={onZoomOut}
                  >
                    <i className="far fa-search-minus" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-rotate-left-button" style={{ margin: 0 }}>Rotate Left</Tooltip>}
                >
                  <Button
                    name="btnRotateLeftImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={onRotateLeft}
                  >
                    <i className="far fa-undo-alt" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-rotate-right-button" style={{ margin: 0 }}>Rotate Right</Tooltip>}
                >
                  <Button
                    name="btnRotateRightImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={onRotateRight}
                  >
                    <i className="far fa-redo-alt" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-fip-horizontal-button" style={{ margin: 0 }}>Fip Horizontal</Tooltip>}
                >
                  <Button
                    name="btnFlipHorizontalImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={onFlipH}
                  >
                    <i className="far fa-arrows-alt-h" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-fip-vertical-button" style={{ margin: 0 }}>Fip Vertical</Tooltip>}
                >
                  <Button
                    name="btnFlipVerticalImage"
                    className="btn btn-primary imageCropperToolbarButton"
                    onClick={onFlipV}
                  >
                    <i className="far fa-arrows-alt-v" />
                  </Button>
                </OverlayTrigger>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImageCropperModal;
